import React, { useState } from 'react';
import ReactDOM from 'react-dom';

const TAG_COLORS = {
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', close: 'text-purple-400 hover:text-purple-700' },
    green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', close: 'text-green-400 hover:text-green-700' },
};

const TagInput = ({ label, items, input, setInput, onAdd, onRemove, placeholder, color }) => {
    const c = TAG_COLORS[color] || TAG_COLORS.purple;
    return (
        <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <div className="flex gap-2 mb-2">
                <input type="text" value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={placeholder} />
                <button type="button" onClick={onAdd}
                    className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add</button>
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                {items.map((v, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 ${c.bg} ${c.text} rounded text-xs font-medium border ${c.border}`}>
                        {v}
                        <button type="button" onClick={() => onRemove(v)} className={c.close}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </span>
                ))}
                {items.length === 0 && <span className="text-xs text-gray-400">None added</span>}
            </div>
        </div>
    );
};

export default function TechStackModal({ firm, techStack, onSave, onClose }) {
    const [formData, setFormData] = useState(techStack || {
        executionType: '', fpgaEnabled: false, programmingLanguages: [], dataSets: [], notes: ''
    });
    const [languageInput, setLanguageInput] = useState('');
    const [dataSetInput, setDataSetInput] = useState('');
    const [saving, setSaving] = useState(false);

    const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const handleSubmit = async () => {
        try { setSaving(true); await onSave(formData); }
        catch (error) { alert('Failed to save tech stack: ' + error.message); }
        finally { setSaving(false); }
    };

    const addTag = (field, input, setInput) => {
        const v = input.trim();
        if (v && !formData[field].includes(v)) { set(field, [...formData[field], v]); setInput(''); }
    };

    const removeTag = (field, val) => set(field, formData[field].filter(x => x !== val));

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }}
            className="bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
                    <div>
                        <h3 className="font-semibold text-gray-900">{techStack ? 'Edit Tech Stack' : 'Add Tech Stack'}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{firm?.name || 'Firm'} technology profile</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Execution Type</label>
                            <select value={formData.executionType} onChange={e => set('executionType', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                <option value="">Select type</option>
                                <option value="centralized">Centralized</option>
                                <option value="decentralized">Decentralized</option>
                                <option value="hybrid">Hybrid</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">FPGA Enabled</label>
                            <div className="flex items-center gap-4 h-[34px]">
                                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                    <input type="radio" checked={formData.fpgaEnabled === true} onChange={() => set('fpgaEnabled', true)} className="text-blue-600 focus:ring-blue-500" /> Yes
                                </label>
                                <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                    <input type="radio" checked={formData.fpgaEnabled === false} onChange={() => set('fpgaEnabled', false)} className="text-blue-600 focus:ring-blue-500" /> No
                                </label>
                            </div>
                        </div>
                    </div>

                    <TagInput label="Programming Languages" items={formData.programmingLanguages} input={languageInput} setInput={setLanguageInput}
                        onAdd={() => addTag('programmingLanguages', languageInput, setLanguageInput)} onRemove={(v) => removeTag('programmingLanguages', v)} placeholder="e.g., Python, C++" color="purple" />
                    <TagInput label="Data Sets" items={formData.dataSets} input={dataSetInput} setInput={setDataSetInput}
                        onAdd={() => addTag('dataSets', dataSetInput, setDataSetInput)} onRemove={(v) => removeTag('dataSets', v)} placeholder="e.g., Bloomberg, Reuters" color="green" />

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                        <textarea value={formData.notes} onChange={e => set('notes', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows="2" placeholder="Additional notes..." />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t flex items-center justify-end gap-2 flex-shrink-0 bg-gray-50 rounded-b-xl">
                    <button type="button" onClick={onClose}
                        className="px-3.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancel</button>
                    <button type="button" onClick={handleSubmit} disabled={saving}
                        className="px-3.5 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Tech Stack'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
